import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface PaginationProps {
  page: number;
  totalPages: number;
  totalElements: number;
  size: number;
  onPageChange: (page: number) => void;
  className?: string;
}

function pageWindow(page: number, totalPages: number): number[] {
  const windowSize = 5;
  let start = Math.max(0, page - Math.floor(windowSize / 2));
  const end = Math.min(totalPages, start + windowSize);
  start = Math.max(0, end - windowSize);
  return Array.from({ length: end - start }, (_, i) => start + i);
}

export function Pagination({ page, totalPages, totalElements, size, onPageChange, className }: PaginationProps) {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  const from = page * size + 1;
  const to = Math.min(totalElements, (page + 1) * size);

  return (
    <div className={cn('flex flex-col sm:flex-row items-center justify-between gap-3 px-1 pt-3', className)}>
      <p className="text-xs text-gray-500">
        {t('pagination.showing', 'Showing {{from}}-{{to}} of {{total}}', { from, to, total: totalElements })}
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
          aria-label={t('pagination.previous', 'Previous page')}
        >
          <ChevronLeft size={16} />
        </button>
        {pageWindow(page, totalPages).map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={cn(
              'min-w-[2rem] h-8 px-2 rounded-lg text-xs font-medium transition',
              p === page
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:bg-gray-50 border border-transparent',
            )}
          >
            {p + 1}
          </button>
        ))}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
          className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
          aria-label={t('pagination.next', 'Next page')}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
